import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import apiClient from '../../request/http-common';

const JointAccountApprovalAdmin = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  
  // Get user role from Redux state
  const userRole = useSelector((state) => state.user.currentRole);
  const userEmail = useSelector((state) => state.user.email);

  console.log('Current user role:', userRole);
  console.log('Current user email:', userEmail);

  // Check if user is admin (either by role or email)
  const isAdmin = userRole === 'admin' || userEmail === 'uloubi7@gmail.com';

  const { data: pendingAccounts, isLoading, error } = useQuery(
    'adminPendingJointAccounts',
    async () => {
      console.log('Fetching pending joint accounts...');
      const response = await apiClient.get('/api/admin/joint-accounts/pending');
      console.log('API Response:', response.data);
      return response.data;
    },
    {
      refetchInterval: 30000, // Refetch every 30 seconds
      onError: (error) => {
        console.error('Admin Joint Account Approvals API Error:', error);
      },
      enabled: isAdmin // Only run query if user is admin
    }
  );

  const approveMutation = useMutation(
    async ({ accountId, adminNotes }) => {
      console.log('Approving account:', accountId, 'with notes:', adminNotes);
      const response = await apiClient.post(`/api/admin/joint-accounts/${accountId}/approve`, {
        admin_notes: adminNotes
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        console.log('Approval successful:', data);
        queryClient.invalidateQueries('adminPendingJointAccounts');
        queryClient.invalidateQueries('jointAccounts');
        setDialogOpen(false);
        setSelectedAccount(null);
        setAdminNotes('');
      },
      onError: (error) => {
        console.error('Admin approval failed:', error);
        alert('Failed to approve joint account: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const rejectMutation = useMutation(
    async ({ accountId, adminNotes }) => {
      console.log('Rejecting account:', accountId, 'with notes:', adminNotes);
      const response = await apiClient.post(`/api/admin/joint-accounts/${accountId}/reject`, {
        admin_notes: adminNotes
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        console.log('Rejection successful:', data);
        queryClient.invalidateQueries('adminPendingJointAccounts');
        queryClient.invalidateQueries('jointAccounts');
        setDialogOpen(false);
        setSelectedAccount(null);
        setAdminNotes('');
      },
      onError: (error) => {
        console.error('Admin rejection failed:', error);
        alert('Failed to reject joint account: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const handleAdminAction = (action) => {
    console.log('Handling admin action:', action, 'for account:', selectedAccount);
    if (selectedAccount) {
      if (action === 'approve') {
        approveMutation.mutate({
          accountId: selectedAccount.id,
          adminNotes
        });
      } else {
        rejectMutation.mutate({
          accountId: selectedAccount.id,
          adminNotes
        });
      }
    }
  };

  const openDialog = (account) => {
    console.log('Opening dialog for account:', account);
    setSelectedAccount(account);
    setDialogOpen(true);
  };

  const formatPrice = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);

  console.log('Component render state:', {
    isLoading,
    error: error?.message,
    pendingAccounts: pendingAccounts?.pending_accounts?.length || 0,
    dialogOpen,
    selectedAccount: selectedAccount?.id,
    isAdmin,
    userRole,
    userEmail
  });

  // Show access denied message if user is not admin
  if (!isAdmin) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Access Denied
          </Typography>
          <Typography variant="body2">
            You need administrator privileges to access this page. 
            Please log in with an admin account.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
            Current user: {userEmail} (Role: {userRole || 'None'})
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load pending joint accounts: {error.message}
        </Alert>
      </Box>
    );
  }

  const accounts = pendingAccounts?.pending_accounts || [];
  console.log('Accounts to render:', accounts.length);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ 
        color: '#FFFFFF', 
        fontWeight: 700, 
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <AdminIcon sx={{ color: '#F7931A' }} />
        Joint Account Approval Dashboard
      </Typography>
      
      <Typography variant="body1" sx={{ color: '#6C7293', mb: 3 }}>
        Review and approve or reject pending joint account creation requests.
      </Typography>

      {accounts.length === 0 ? (
        <Card sx={{ 
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
          border: '1px solid #2B3139'
        }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <AccountBalanceIcon sx={{ fontSize: 64, color: '#6C7293', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 1 }}>
              No Pending Joint Accounts
            </Typography>
            <Typography variant="body2" sx={{ color: '#6C7293' }}>
              All joint account requests have been processed.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List sx={{ width: '100%' }}>
          {accounts.map((account) => (
            <Card key={account.id} sx={{ 
              mb: 3,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
              border: '2px solid #F7931A'
            }}>
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: '#F7931A' }}>
                    <AccountBalanceIcon />
                  </Avatar>
                </ListItemAvatar>
                
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                        {account.account_name}
                      </Typography>
                      <Chip 
                        label="Pending Approval" 
                        color="warning" 
                        size="small" 
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                        Account #{account.account_number}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                        Created by: {account.creator?.name || 'Unknown'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                        Minimum Approval Amount: {formatPrice(account.minimum_approval_amount)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                        Approval Type: {account.approval_type.replace('_', ' ')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <PeopleIcon sx={{ fontSize: 16, color: '#6C7293' }} />
                        <Typography variant="body2" sx={{ color: '#6C7293' }}>
                          {account.members?.length || 0} members
                        </Typography>
                      </Box>
                      {account.description && (
                        <Typography variant="body2" sx={{ color: '#6C7293', mt: 1 }}>
                          Description: {account.description}
                        </Typography>
                      )}
                      
                      {/* Member Details */}
                      {account.members && account.members.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" sx={{ color: '#6C7293', fontWeight: 600, display: 'block', mb: 0.5 }}>
                            Members:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {account.members.map((member, index) => (
                              <Chip
                                key={index}
                                label={`${member.user?.name}: ${member.role}`}
                                color={member.status === 'active' ? 'success' : 'warning'}
                                size="small"
                                sx={{ 
                                  fontSize: '0.7rem',
                                  height: '20px',
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  }
                />
                
                <Button
                  variant="contained"
                  onClick={() => openDialog(account)}
                  sx={{
                    background: 'linear-gradient(135deg, #F7931A 0%, #FFB800 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #E8851A 0%, #E8A800 100%)'
                    }
                  }}
                >
                  Review & Approve
                </Button>
              </ListItem>
            </Card>
          ))}
        </List>
      )}

      {/* Admin Action Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#FFFFFF', background: '#1a1a1a' }}>
          Joint Account Review - {selectedAccount?.account_name}
        </DialogTitle>
        <DialogContent sx={{ background: '#1a1a1a', color: '#FFFFFF' }}>
          {selectedAccount && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Account Name: {selectedAccount.account_name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Created by: {selectedAccount.creator?.name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Account Number: #{selectedAccount.account_number}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Members: {selectedAccount.members?.length || 0}
              </Typography>
              {selectedAccount.description && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Description: {selectedAccount.description}
                </Typography>
              )}
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Admin Notes (Optional)"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': {
                      borderColor: '#6C7293',
                    },
                    '&:hover fieldset': {
                      borderColor: '#F7931A',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#6C7293',
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ background: '#1a1a1a', p: 2, gap: 1 }}>
          <Button 
            onClick={() => setDialogOpen(false)}
            sx={{ color: '#6C7293' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handleAdminAction('reject')}
            variant="outlined"
            color="error"
            disabled={approveMutation.isLoading || rejectMutation.isLoading}
            sx={{
              borderColor: '#F84960',
              color: '#F84960',
              '&:hover': {
                borderColor: '#DC2626',
                backgroundColor: 'rgba(248, 73, 96, 0.1)'
              }
            }}
          >
            Reject
          </Button>
          <Button 
            onClick={() => handleAdminAction('approve')}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
              }
            }}
            disabled={approveMutation.isLoading || rejectMutation.isLoading}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JointAccountApprovalAdmin; 